import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Send as SendIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Schedule as ScheduleIcon,
  Email as EmailIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { campaignApi, getDefaultAccountId } from '../services/api';
import { AlertInfo } from '../types';
import CampaignDetail from './CampaignDetail';

interface CampaignsProps {
  showAlert: (message: string, severity: AlertInfo['severity']) => void;
  onNavigate?: (view: string) => void;
}

interface Campaign {
  id: string;
  name: string;
  subject: string;
  status: 'DRAFT' | 'SCHEDULED' | 'SENDING' | 'SENT' | 'PAUSED' | 'CANCELLED';
  isFollowUp: boolean;
  scheduledAt?: string;
  sentAt?: string;
  createdAt: string;
  updatedAt: string;
  recipients: any[];
  followUpCampaigns?: Array<{
    id: string;
    name: string;
    status: string;
    createdAt: string;
  }>;
  analytics?: {
    totalSent: number;
    totalOpened: number;
    totalClicked: number;
    openRate: number;
    clickRate: number;
  };
}

const Campaigns: React.FC<CampaignsProps> = ({ showAlert, onNavigate }) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<Campaign | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const accountId = getDefaultAccountId();
      const response = await campaignApi.getByAccount(accountId);
      setCampaigns(response.campaigns || []);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      showAlert('Failed to load campaigns. Please check your connection.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, campaign: Campaign) => {
    setAnchorEl(event.currentTarget);
    setSelectedCampaign(campaign);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCampaign(null);
  };

  const handleDeleteClick = (campaign: Campaign) => {
    setCampaignToDelete(campaign);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleViewCampaign = (campaignId: string) => {
    setSelectedCampaignId(campaignId);
    setViewMode('detail');
    handleMenuClose();
  };

  const handleEditCampaign = (campaign: Campaign) => {
    // Navigate to compose view with campaign data for editing
    if (onNavigate) {
      // Store campaign data in localStorage for the compose component to pick up
      localStorage.setItem('editCampaign', JSON.stringify(campaign));
      onNavigate('compose');
    }
    handleMenuClose();
  };

  const handleDuplicateCampaign = async (campaign: Campaign) => {
    try {
      // Create a duplicate campaign with modified name
      const duplicateData = {
        ...campaign,
        name: `${campaign.name} (Copy)`,
        status: 'DRAFT' as const,
        id: undefined, // Let the API generate a new ID
        createdAt: undefined,
        updatedAt: undefined,
        sentAt: undefined,
        scheduledAt: undefined,
      };

      await campaignApi.create({
        ...duplicateData,
        accountId: getDefaultAccountId()
      });

      showAlert('Campaign duplicated successfully', 'success');
      loadCampaigns(); // Refresh the list
    } catch (error) {
      console.error('Error duplicating campaign:', error);
      showAlert('Failed to duplicate campaign. Please try again.', 'error');
    }
    handleMenuClose();
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedCampaignId(null);
    loadCampaigns(); // Refresh the list
  };

  const handleDeleteConfirm = async () => {
    if (!campaignToDelete) return;

    try {
      await campaignApi.delete(campaignToDelete.id);
      showAlert('Campaign deleted successfully', 'success');
      loadCampaigns();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      showAlert('Failed to delete campaign. Please try again.', 'error');
    } finally {
      setDeleteDialogOpen(false);
      setCampaignToDelete(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SENT': return 'success';
      case 'SENDING': return 'info';
      case 'SCHEDULED': return 'warning';
      case 'DRAFT': return 'default';
      case 'PAUSED': return 'warning';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Calculate summary stats
  const totalCampaigns = campaigns.length;
  const sentCampaigns = campaigns.filter(c => c.status === 'SENT').length;
  const draftCampaigns = campaigns.filter(c => c.status === 'DRAFT').length;
  const scheduledCampaigns = campaigns.filter(c => c.status === 'SCHEDULED').length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show campaign detail view
  if (viewMode === 'detail' && selectedCampaignId) {
    return (
      <CampaignDetail
        campaignId={selectedCampaignId}
        onBack={handleBackToList}
        showAlert={showAlert}
      />
    );
  }

  return (
    <Box sx={{ height: '100%' }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            Campaigns
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and track your email campaigns
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ borderRadius: 3, px: 3 }}
          onClick={() => onNavigate?.('compose')}
        >
          New Campaign
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #e3f2fd 0%, white 100%)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {totalCampaigns}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Campaigns
                  </Typography>
                </Box>
                <EmailIcon sx={{ fontSize: 40, color: '#1976d2' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #e8f5e8 0%, white 100%)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {sentCampaigns}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sent
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 40, color: '#388e3c' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #fff3e0 0%, white 100%)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {draftCampaigns}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Drafts
                  </Typography>
                </Box>
                <EditIcon sx={{ fontSize: 40, color: '#f57c00' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #f3e5f5 0%, white 100%)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {scheduledCampaigns}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Scheduled
                  </Typography>
                </Box>
                <ScheduleIcon sx={{ fontSize: 40, color: '#7b1fa2' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Campaigns Table */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Campaign Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Subject</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Recipients</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Open Rate</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map((campaign) => (
                <TableRow key={campaign.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {campaign.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                        {campaign.isFollowUp && (
                          <Chip label="Follow-up" size="small" color="secondary" />
                        )}
                        {campaign.followUpCampaigns && campaign.followUpCampaigns.length > 0 && (
                          <Chip
                            label="Pending Follow-up"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap>
                      {campaign.subject}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={campaign.status}
                      color={getStatusColor(campaign.status) as any}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {campaign.recipients?.length || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {campaign.analytics?.openRate ? `${campaign.analytics.openRate.toFixed(1)}%` : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(campaign.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="More actions">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, campaign)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {campaigns.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No campaigns found. Create your first campaign to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => selectedCampaign && handleViewCampaign(selectedCampaign.id)}>
          <ViewIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        {/* Edit button - only visible for DRAFT campaigns */}
        {selectedCampaign?.status === 'DRAFT' && (
          <MenuItem onClick={() => selectedCampaign && handleEditCampaign(selectedCampaign)}>
            <EditIcon sx={{ mr: 1 }} />
            Edit
          </MenuItem>
        )}
        {/* Duplicate button - only visible for SENT campaigns */}
        {selectedCampaign?.status === 'SENT' && (
          <MenuItem onClick={() => selectedCampaign && handleDuplicateCampaign(selectedCampaign)}>
            <SendIcon sx={{ mr: 1 }} />
            Duplicate
          </MenuItem>
        )}
        {/* Delete button - only visible for DRAFT campaigns */}
        {selectedCampaign?.status === 'DRAFT' && (
          <MenuItem onClick={() => selectedCampaign && handleDeleteClick(selectedCampaign)}>
            <DeleteIcon sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Campaign</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{campaignToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Campaigns;
